import { Translate } from 'hooks/use-translation';

export const typecastError = (
  error: unknown,
  fallbackString?: string,
): Error => {
  try {
    if (typeof error === 'string') {
      return new Error(error);
    }
    if (error instanceof Error) {
      return error;
    }
    if (typeof error === 'object') {
      return new Error(JSON.stringify(error));
    }

    const err = new Error(fallbackString ?? 'unhandled error');

    (err as any).cause = error;

    return err;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return new Error('Error when typecasting error');
  }
};

export const createAlreadyExistsError = (
  translate: Translate,
  value: string,
): Promise<never> => {
  return Promise.reject(
    new Error(translate('error.already_exists', { fieldName: value })),
  );
};

export const extractApiErrorMessage = (
  error: unknown,
  fallback = '',
): string => {
  const err = error as {
    response?: {
      data?: { errors?: Array<{ reason?: string }> };
      message?: string;
    };
    message?: string;
  };

  return (
    err?.response?.data?.errors?.[0]?.reason ||
    err?.response?.message ||
    err?.message ||
    fallback
  );
};

export const extractApiErrorCode = (error: unknown, fallback = ''): string => {
  const err = error as {
    response?: {
      data?: { errors?: Array<{ code?: string }> };
    };
  };

  return err?.response?.data?.errors?.[0]?.code || fallback;
};

/** Backend WeakPasswordException messages – we show one unified message for any of these. */
const PASSWORD_VALIDATION_REASONS = new Set([
  'Password cannot be empty.',
  'Password must be at least 11 characters long.',
  'Password must contain at least one uppercase letter.',
  'Password must contain at least one lowercase letter.',
  'Password must contain at least one number.',
  'Password must contain at least one special character.',
  'Password is too weak or commonly used.',
]);

export interface PasswordFormErrorMessages {
  primary: string;
  secondary?: string;
}

/**
 * For password flows only:
 * - when reason is in PASSWORD_VALIDATION_REASONS, returns both a unified message (primary)
 *   and specific reason (secondary) so callers can show two toasts.
 * - otherwise returns only primary with API message/fallback.
 */
export const getPasswordFormErrorMessage = (
  error: unknown,
  unifiedMessage: string,
  fallback: string,
): PasswordFormErrorMessages => {
  const err = error as {
    response?: { data?: { errors?: Array<{ reason?: string }> } };
  };
  const reason = err?.response?.data?.errors?.[0]?.reason;

  if (reason != null && PASSWORD_VALIDATION_REASONS.has(reason)) {
    return {
      primary: unifiedMessage,
      secondary: reason,
    };
  }

  if (reason != null) {
    return {
      primary: extractApiErrorMessage(error, fallback),
    };
  }

  return {
    primary: fallback,
  };
};
